/**
 * Image-URL safety guard — defense-in-depth for backend#364.
 *
 * The inventory API can transiently return a raw `gs://…` object path
 * for a freshly-generated AI cutout (`primary_photo_url`) before the
 * async crop→sign step finishes, and a few legacy rows carry malformed
 * URLs. A React Native `<Image>` can only load `http(s)` URIs — a
 * `gs://` or malformed value renders blank AND fails with no HTTP status
 * (`net::ERR_UNKNOWN_URL_SCHEME`), so it's invisible to Sentry.
 *
 * These helpers ensure we only ever hand the render layer a usable
 * `http(s)` URL, falling back through the candidate chain otherwise.
 */

/** True only for a non-empty `http(s)://` string. */
export function isHttpUrl(u: unknown): u is string {
  return typeof u === 'string' && /^https?:\/\//i.test(u);
}

/**
 * Returns the first usable `http(s)` URL among the candidates, else
 * `undefined`. Use this wherever an image URI is chosen from a fallback
 * chain (e.g. `primary_photo_url || source_image_url || image_url`) so a
 * transient `gs://` / malformed value is skipped instead of rendered.
 */
export function httpImageUri(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const c of candidates) {
    if (isHttpUrl(c)) return c;
  }
  return undefined;
}
